// src/api/impresoraFiscal.js - Cliente para comunicarse con SystemJJ.FiscalPrinterBridge (Asistente Local o Relay Backend)
import { apiFetch } from './index.js';

const ASISTENTE_DEFAULT_URL = 'http://localhost:5000';
const STORAGE_KEY = 'fiscal_printer_bridge_url';

export const getAsistenteUrl = () => {
  return localStorage.getItem(STORAGE_KEY) || ASISTENTE_DEFAULT_URL;
};

export const setAsistenteUrl = (url) => {
  if (!url) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    let cleanUrl = url.trim();
    if (cleanUrl.endsWith('/')) {
      cleanUrl = cleanUrl.slice(0, -1);
    }
    localStorage.setItem(STORAGE_KEY, cleanUrl);
  }
};

/**
 * Petición genérica al asistente: soporta comunicación directa al Bridge HTTP
 * o mediante el Backend Web (modo Relay vía Tailscale para producción/nube).
 */
const requestFiscal = async (backendEndpoint, bridgeEndpoint, options = {}, timeoutMs = 10000) => {
  const configuredUrl = getAsistenteUrl();
  const esModoBackend = configuredUrl === 'backend' || configuredUrl.startsWith('api') || (typeof window !== 'undefined' && window.location.protocol === 'https:' && configuredUrl.startsWith('http://'));

  const inicio = performance.now();

  // MODO 1: A través del Backend Web (Recomendado para la Nube con Tailscale o HTTPS)
  if (esModoBackend) {
    try {
      const response = await apiFetch(`/ImpresionFiscal${backendEndpoint}`, options);
      const tiempoMs = Math.round(performance.now() - inicio);

      return {
        exito: response?.exito !== undefined ? response.exito : true,
        mensaje: response?.mensaje || 'Operación completada vía backend',
        data: response?.data || response,
        tiempoMs,
        viaBackend: true
      };
    } catch (error) {
      const tiempoMs = Math.round(performance.now() - inicio);
      return {
        exito: false,
        mensaje: error.message || 'Error comunicando con el servicio fiscal del backend',
        tiempoMs,
        viaBackend: true
      };
    }
  }

  // MODO 2: Conexión Directa al Bridge (Red Local / IP Tailscale directa)
  const url = `${configuredUrl}${bridgeEndpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    const tiempoMs = Math.round(performance.now() - inicio);

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = { mensaje: response.statusText };
    }

    if (!response.ok) {
      return {
        exito: false,
        mensaje: data.error || data.mensaje || `Error HTTP ${response.status}`,
        tiempoMs,
        status: response.status
      };
    }

    return {
      exito: data.exito !== undefined ? data.exito : true,
      mensaje: data.mensaje || 'Operación completada en asistente local',
      data,
      tiempoMs,
      status: response.status
    };
  } catch (error) {
    clearTimeout(timeoutId);
    const tiempoMs = Math.round(performance.now() - inicio);

    if (error.name === 'AbortError') {
      return {
        exito: false,
        mensaje: `Tiempo de espera agotado (${timeoutMs / 1000}s). La impresora o el asistente no respondieron.`,
        tiempoMs,
        esTimeout: true
      };
    }

    return {
      exito: false,
      mensaje: 'No se pudo conectar con el bridge fiscal. Verifique que esté encendida la impresora y ejecutándose el servicio SystemJJ.FiscalPrinterBridge.',
      tiempoMs,
      errorOriginal: error.message
    };
  }
};

/**
 * Probar conexión con la impresora y verificar si el puerto COM está abierto
 */
export const probarConexionImpresora = async () => {
  return await requestFiscal('/test', '/api/impresora/test', { method: 'GET' }, 5000);
};

/**
 * Imprimir Reporte X (Corte parcial sin cierre de jornada)
 */
export const imprimirReporteX = async () => {
  return await requestFiscal('/reporte-x', '/api/impresora/reporte-x', { method: 'POST' }, 10000);
};

/**
 * Imprimir Cierre Z (Cierre fiscal diario definitivo)
 */
export const imprimirCierreZ = async () => {
  return await requestFiscal('/cierre-z', '/api/impresora/cierre-z', { method: 'POST' }, 15000);
};

/**
 * Cancelar factura o documento pendiente que haya quedado trabado
 */
export const cancelarFacturaImpresora = async () => {
  return await requestFiscal('/cancelar', '/api/impresora/cancelar', { method: 'POST' }, 6000);
};

/**
 * Reset suave de la impresora fiscal
 */
export const resetSuaveImpresora = async () => {
  return await requestFiscal('/reset', '/api/impresora/reset', { method: 'POST' }, 8000);
};

/**
 * Imprimir factura fiscal completa
 */
export const imprimirFacturaFiscal = async (facturaData) => {
  return await requestFiscal('/factura', '/api/impresora/factura', {
    method: 'POST',
    body: JSON.stringify(facturaData)
  }, 20000);
};

export default {
  getAsistenteUrl,
  setAsistenteUrl,
  probarConexionImpresora,
  imprimirReporteX,
  imprimirCierreZ,
  cancelarFacturaImpresora,
  resetSuaveImpresora,
  imprimirFacturaFiscal
};
